import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { Menu } from 'antd';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import GroupSelect from '@src/components/groupSelect';
import { ICategory, IHierarchy } from '@src/types/category';
import { getTags } from '@src/store/selectors/event';
import { selectSchool } from '@src/store/selectors/school';
import { getIsTagVisible, getIsTeacherFilterVisible } from '@src/utils/hardcode';
import * as selectors from '@src/store/selectors';

import './global.module.less';

const { SubMenu } = Menu;

const Content = styled.div`
	width: 224px;
	position: relative;
`;

const Wrap = styled.div`
	width: 240px;
	padding: 0px 20px 70px 20px;
	height: 100%;
`;

const StyledMenu = styled(Menu).attrs({ className: 'menu' })`
	width: 224px;
	font-size: 16px;
	color: #101010;
	padding-top: 24px;

	&.ant-menu {
		border-right: unset;
	}

	&.ant-menu .ant-menu-submenu-title {
		display: flex;
		align-items: center;
		height: 24px !important;
		line-height: 24px !important;
		margin-top: 0px;
		margin-bottom: 8px;
	}

	&.ant-menu .ant-menu-item {
		height: 24px;
		line-height: 24px !important;
		margin-top: 0px;
		margin-bottom: 10px;
	}

	&.ant-menu .ant-menu-submenu-title:active {
		background: #FFFFFF !important;
	}

	&.ant-menu:hover .ant-menu-submenu-arrow {
		color: #333;
	}

	&.ant-menu:hover .ant-menu-submenu-active > .ant-menu-submenu-title > .ant-menu-submenu-arrow {
		color: ${props => props.theme.colors.primary} !important;
	}
`;

const StyledSubMenu = styled(SubMenu)`
	&	.ant-menu-sub {
		background: #FFFFFF;
	}
`;

const StyledMenuItem = styled(Menu.Item)`
	&.ant-menu-item {
		/* padding-left: 18px !important; */
		display: flex;
		align-items: center;
		background-color: #FFFFFF !important;
	}

	&.ant-menu-item::after {
		border-right: unset;
	}

	&.ant-menu-item.ant-menu-item-selected.level2 h2 {
		color: ${props => props.theme.colors.primary} !important;
	}

	&.level2 {
		padding-left: 48px  !important;
	}

	&.ant-menu-submenu:hover .ant-menu-submenu-arrow {
		color: #333 !important;
	}

	&.ant-menu-submenu:hover .ant-menu-submenu-active .ant-menu-submenu-arrow {
		color: ${props => props.theme.colors.primary} !important;
	}
`;

const StyledH2 = styled.h2`
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
	margin-bottom: unset;
	color: ${props => props.isSelected && props.theme.colors.primary} !important;
	:hover {
		color: ${props => props.theme.colors.primary} !important;
	}
	${props => {
		if (props.level === 0) return 'color: #101010;';
		else if (props.level === 1) return 'color: #676767;';
		else {
			return `
				font-weight: 300 !important;
				color: #676767;
			`;
		}
	}};
	white-space: pre-wrap;
	padding-right: 36px !important;
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
`;
const ChipsWrapper = styled.div`
	margin-top: -4px;

	& > *:not(:last-child) {
		margin-right: 8px;
	}
`;

const Chip = styled.div`
	cursor: pointer;
	display: inline-flex;
	padding: 4px 9px 4px 12px;
	border-radius: 50px;
	border: 1px solid;
	border-color: ${props => (props.isSelect ? props.theme.colors.primary : '#DDDDDD')};
	margin-top: 4px;
	line-height: 20px;
	font-size: 14px;
	color: ${props => (props.isSelect ? '#fff' : '#101010')};
	background-color: ${props => (props.isSelect ? props.theme.colors.primary : '#fff')};

	:hover {
		border-color: ${props => props.theme.colors.primary};
	}
`;

const Divider = styled.div`
	border-top: 1px solid #E9E9E9;
	margin: 0px 12px;
`;

const OptionContent = styled.div`
	margin: 0 12px;
`;

const TitleContent = styled.div`
	font-size: 16px;
	color: #101010;
	margin-bottom: 12px;
`;

const OptionWrap = styled.div`
	margin: 24px 0;
`;

const BottomLineText = styled.div`
	font-size: 14px;
	text-decoration-line: underline;
	color: #333333;
	cursor: pointer;
	display: inline-flex;
`;

const ResetFilterText = styled.div`
	margin-top: 20px;
	font-size: 14px;
	text-decoration-line: underline;
	color: #9C9C9C;
	cursor: pointer;
	@media
		${props => props.theme.device.mobile},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet},
	{
		display: none;
	}
`;

const MobileActionContent = styled.div`
	display: none;
	@media
		${props => props.theme.device.mobile},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet}
	{
		display: block;
		position: fixed;
		bottom: 0;
		width: 100%;
		background: #FFFFFF;
		box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
	}
`;

const MobileActionWrap = styled.div`
	height: 70px;
	padding: 8px 12px 8px 0;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
`;

const ResetFilterBtn = styled(ResetFilterText)`
	margin-top: unset !important;
	width: 50%;
	display: flex !important;
	justify-content: center;
	align-items: center;
`;

const ApplyFilterBtn = styled.div`
	color: #FFFFFF;
	width: 50%;
	max-width: 314px;
	font-size: 20px;
	font-weight: 500;
	background: ${props => props.theme.colors.primary};
	border-radius: 4px;
	padding: 8px 12px;
	display: flex;
	justify-content: center;
	align-items: center;
`;

// eslint-disable-next-line react/display-name
const MenuContent = forwardRef((
	{
		className,
		categories,
		hierarchy,
		onFilterChange,
		breadcrumbPath = [],
		visible,
		setVisible,
		publishedType = [],
		resetFilter,
	}:
	{
		className : string;
		categories: ICategory[];
		hierarchy: IHierarchy[];
		onFilterChange: ({
			commodity_type,
			tag_ids,
			teacher_id,
			category_short_link,
		}: {
			commodity_type?: string;
			tag_ids?: string;
			teacher_id?: string;
			category_short_link?: string;
			sort_type?: string;
			page?: number;
			page_size?: number;
		}) => void;
		breadcrumbPath: IHierarchy[];
		visible?: boolean;
		setVisible: (boolean) => void;
		publishedType: string[];
		resetFilter: () => void;
	},
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	ref,
): JSX.Element => {
	const { t } = useTranslation();
	const router = useRouter();
	const { shortLink, commodity_type = '', tag_ids = '' as string, teacher_id = '' } = router.query;
	const [commodityType, setCommodityType] = useState(commodity_type as string);
	const [link, setShortLink] = useState(shortLink as string);
	const [teacherId, setTeacherId] = useState(teacher_id as string);
	const tagIds = Array.isArray(tag_ids) ? tag_ids[0] : tag_ids;
	const [selectTags, setSelectTagIds] = useState(tagIds.split(','));
	const [isExpand, setIsExpand] = useState(false);
	const tags = useSelector(getTags);
	const { teachers } = useSelector(selectSchool);
	const rootSubMenuKeys = hierarchy.map(h => {
		const category = _.find(categories, { id: h.id });
		return category?.short_link || 'all';
	});
	const [openKeys, setOpenKeys] = useState([]);
	const domain = useSelector(selectors.getDomain) as string;
	const isTagFilterVisible = getIsTagVisible(domain);
	const isTeacherFilterVisible = getIsTeacherFilterVisible(domain);

	const onOpenChange = keys => {
		const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
		if (rootSubMenuKeys.indexOf(latestOpenKey) === -1) {
			setOpenKeys(keys);
		} else {
			setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
		}
	};

	const onSelectCategory = ({ key }) => setShortLink(key);

	const renderMenu = ({ list = [], level = 0, selected }) => (
		list.map(item => {
			const category = _.find(categories, { id: item.id });
			if (item.children) {
				return (
					<StyledSubMenu
						key={category.short_link}
						title={(
							<StyledH2
								isSelected={selected === category.short_link}
								level={level}
								onClick={() => onSelectCategory({ key: category.short_link })}
							>
								{category.name}
							</StyledH2>
						)}
					>
						{renderMenu({ list: item.children, level: level + 1, selected })}
					</StyledSubMenu>
				);
			} else {
				return (
					<StyledMenuItem key={category.short_link} className={`level${level}`}>
						<StyledH2
							isSelected={selected === category.short_link}
							level={level}
							onClick={() => onSelectCategory({ key: category.short_link })}
						>
							{level === 2 ? `- ${category.name}` : category.name}
						</StyledH2>
					</StyledMenuItem>
				);
			}
		})
	);

	const onSelectTag = id => {
		if (selectTags.indexOf(id) === -1) {
			setSelectTagIds(prevTags => [...prevTags, id]);
		} else {
			setSelectTagIds(prevTags => _.filter(prevTags, tagId => tagId !== id));
		}
	};

	useEffect(() => {
		const type = commodityType === 'all' ? '' : commodityType;
		const teacher = teacherId === 'all' ? '' : teacherId;
		onFilterChange({
			commodity_type: type,
			tag_ids: selectTags.join(','),
			teacher_id: teacher,
			category_short_link: link,
		});
	}, [commodityType, link, teacherId, selectTags]);

	useImperativeHandle(ref, () => ({
		reset() {
			setShortLink(() => 'all');
			setCommodityType(() => '');
			setTeacherId(() => '');
			setSelectTagIds(() => []);
			setOpenKeys(['all']);
		},
	}));

	useEffect(() => {
		if (breadcrumbPath.length > 0) {
			const updateOpenKeys = breadcrumbPath.map(path => {
				const category = _.find(categories, { id: path.id });
				return category?.short_link || 'all';
			});
			setOpenKeys(updateOpenKeys);
		}
	}, [breadcrumbPath]);

	return (
		<Content className={className}>
			<Wrap className="wrap">
				<StyledMenu mode="inline" openKeys={openKeys} onOpenChange={onOpenChange} onClick={onSelectCategory}>
					<StyledMenuItem key="all">
						<StyledH2 isSelected={link === 'all'} level={0}>{t('category:all_category')}</StyledH2>
					</StyledMenuItem>
					{renderMenu({ list: hierarchy, selected: link })}
				</StyledMenu>
				<Divider />
				<OptionContent className="option">
					{isTagFilterVisible && (
						<OptionWrap>
							<TitleContent>
								{t('category:filter_category')}
							</TitleContent>
							<GroupSelect
								optionId={commodityType}
								defaultPlaceholder="category:filter_category_placeholder"
								onChange={({ id }) => setCommodityType(id)}
								groupOption={[
									{
										groupTitle: '',
										options: [
											{ id: 'all', title: t('category:filter_category_all') },
											...publishedType.map(type => ({
												id: type, title: t(`category:filter_category_${type}`),
											})),
										],
									},
								]}
							/>
						</OptionWrap>
					)}
					{
						isTeacherFilterVisible && (
							<OptionWrap>
								<TitleContent>
									{t('category:filter_teacher')}
								</TitleContent>
								<GroupSelect
									optionId={teacherId}
									defaultPlaceholder="category:filter_teacher_placeholder"
									onChange={({ id }) => setTeacherId(id)}
									groupOption={[
										{
											groupTitle: '',
											options: [
												{ id: 'all', title: t('category:filter_teacher_all') },
												...teachers.map(teacher => ({ id: teacher.id, title: teacher.name })),
											],
										},
									]}
								/>
							</OptionWrap>
						)
					}
					<OptionWrap>
						<TitleContent>
							{t('category:tag')}
						</TitleContent>
						<ChipsWrapper>
							{
								tags.map((tag, i) => {
									if (!isExpand && i > 5) return null;
									return (
										<Chip
											onClick={() => onSelectTag(tag.id)}
											isSelect={selectTags.indexOf(tag.id) !== -1}
											key={tag.id}
										>
											{`＃${tag.name}`}
										</Chip>
									);
								})
							}
							{
								isExpand ? (
									<BottomLineText onClick={() => setIsExpand(false)}>
										{t('category:collapse')}
									</BottomLineText>
								) : (
									<BottomLineText onClick={() => setIsExpand(true)}>
										{t('category:expansion')}
									</BottomLineText>
								)
							}
						</ChipsWrapper>
						<ResetFilterText onClick={resetFilter}>
							{t('category:clean_all')}
						</ResetFilterText>
					</OptionWrap>
				</OptionContent>
			</Wrap>
			{
				visible && (
					<MobileActionContent>
						<MobileActionWrap>
							<ResetFilterBtn onClick={resetFilter}>
								{t('category:clean_all')}
							</ResetFilterBtn>
							<ApplyFilterBtn onClick={() => setVisible(false)}>
								{t('category:apply_filter_button')}
							</ApplyFilterBtn>
						</MobileActionWrap>
					</MobileActionContent>
				)
			}
		</Content>
	);
});

export default MenuContent;
